import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Check, Pencil, Trash2 } from 'lucide-react';

import { deleteDraftRepairRequest } from '#/api/aftersalesMgmt/repairRequest/deleteDraft';
import { establishRepairRequest } from '#/api/aftersalesMgmt/repairRequest/establish';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  getRepairRequestApiErrorMessage,
  isRepairRequestVersionConflict,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/apiError';
import {
  canDeleteDraftRepairRequest,
  canEditRepairRequest,
  canEstablishRepairRequest,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/repairRequestStatus';
import RepairRequestCorrectionButtonsStandalone, {
  RepairRequestCorrectionButtons,
} from '#/modules/crmMgmt/aftersales/repairRequest/components/RepairRequestCorrectionButtons';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { toast } from '#/utils/toast';
import {
  repairRequestDetailQueryKey,
  repairRequestListQueryKeyPrefix,
} from '#/utils/queryKeys';

const RepairRequestCommandBar = ({
  detail,
  onRefetch,
  onDeleted,
  correctionActions = null,
  inDetailPage = false,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? null;
  const status = detail?.status;

  async function invalidateAll() {
    await queryClient.invalidateQueries({ queryKey: repairRequestListQueryKeyPrefix() });
    if (id) {
      await queryClient.invalidateQueries({ queryKey: repairRequestDetailQueryKey(id) });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'delete') {
        return deleteDraftRepairRequest(id, payload);
      }
      if (action === 'establish') {
        return establishRepairRequest(id, payload);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    retry: false,
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'delete') {
        await queryClient.invalidateQueries({ queryKey: repairRequestListQueryKeyPrefix() });
        onDeleted?.();
        navigate(ROUTES.repairRequest);
        return;
      }
      await invalidateAll();
    },
    onError: (error) => {
      toast.error(getRepairRequestApiErrorMessage(error));
      if (isRepairRequestVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  if (!id) {
    return null;
  }

  async function handleDeleteDraft() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'delete', payload: { updatedAt } });
  }

  async function handleEstablish() {
    const ok = await confirm({
      title: '成立報修單',
      message: '成立後主單進入處理中，並為每筆明細建立待處理維修品。確定成立？',
      confirmText: '成立',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'establish', payload: { updatedAt } });
  }

  return (
    <>
      {ConfirmDialogHost}
      {canEditRepairRequest(status) ? (
        <Button asChild className="document-detail-edit-action">
          <Link to={ROUTES.repairRequestEdit(id)}>
            <Pencil className="size-3.5" aria-hidden />
            編輯
          </Link>
        </Button>
      ) : null}
      {canEstablishRepairRequest(status) ? (
        <Button disabled={isPending} onClick={handleEstablish}>
          <Check className="size-3.5" aria-hidden />
          成立
        </Button>
      ) : null}
      {canDeleteDraftRepairRequest(status) ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={handleDeleteDraft}
        >
          <Trash2 className="size-3.5" aria-hidden />
          刪除草稿
        </Button>
      ) : null}
      {correctionActions ? (
        <RepairRequestCorrectionButtons actions={correctionActions} compact={inDetailPage} />
      ) : (
        <RepairRequestCorrectionButtonsStandalone detail={detail} onRefetch={onRefetch} />
      )}
    </>
  );
};

RepairRequestCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
  onDeleted: PropTypes.func,
  /** 詳情頁由 RepairRequestBlock 共用（明細列也要用）；省略時自建 */
  correctionActions: PropTypes.object,
  /** 詳情頁：新增品項／更正紀錄移到下方分頁，這裡只留更正與完成 */
  inDetailPage: PropTypes.bool,
};

export default RepairRequestCommandBar;
